import React, { useEffect, useState } from 'react';
import { FiX, FiDownload, FiExternalLink, FiEdit2 } from 'react-icons/fi';
import AuthImage from './AuthImage';
import ChatImageEditor from './ChatImageEditor';

interface Props {
  url: string;
  name: string;
  mime: string;
  contactUsername: string;
  onClose: () => void;
  onAddToPatientFile?: (url: string, name: string, mime: string) => void;
  onImageSaved?: (newUrl: string, newName: string) => void;
}

const ChatMediaViewer: React.FC<Props> = ({
  url, name, mime, contactUsername, onClose, onAddToPatientFile, onImageSaved,
}) => {
  const isImage = mime.startsWith('image/');
  const isPdf   = mime === 'application/pdf';
  const [editing, setEditing] = useState(false);
  const [currentUrl, setCurrentUrl]   = useState(url);
  const [currentName, setCurrentName] = useState(name);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape' && !editing) onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, editing]);

  const handleSaved = (newUrl: string, newName: string) => {
    setCurrentUrl(newUrl);
    setCurrentName(newName);
    setEditing(false);
    onImageSaved?.(newUrl, newName);
  };

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex flex-col bg-black/80 backdrop-blur-sm"
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        {/* Toolbar */}
        <div className="flex items-center gap-3 px-4 py-3 bg-base-200/90 flex-shrink-0">
          <span className="flex-1 text-sm font-medium truncate">{currentName}</span>

          {isImage && (
            <button
              className="btn btn-sm btn-info gap-2"
              onClick={() => setEditing(true)}
              title="Edit image"
            >
              <FiEdit2 className="text-sm" />
              Edit Image
            </button>
          )}

          {onAddToPatientFile && (
            <button
              className="btn btn-sm btn-success gap-2"
              onClick={() => onAddToPatientFile(currentUrl, currentName, mime)}
              title="Add to Patient File"
            >
              Add to Patient File
            </button>
          )}

          <a
            href={currentUrl}
            download={currentName}
            className="btn btn-ghost btn-sm btn-square"
            title="Download"
            onClick={e => e.stopPropagation()}
          >
            <FiDownload />
          </a>

          <a
            href={currentUrl}
            target="_blank"
            rel="noreferrer"
            className="btn btn-ghost btn-sm btn-square"
            title="Open in new tab"
            onClick={e => e.stopPropagation()}
          >
            <FiExternalLink />
          </a>

          <button className="btn btn-ghost btn-sm btn-square" onClick={onClose} title="Close (Esc)">
            <FiX />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto flex items-center justify-center p-4">
          {isImage && (
            <AuthImage
              src={currentUrl}
              alt={currentName}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl select-none"
            />
          )}

          {isPdf && (
            <iframe
              src={currentUrl}
              title={currentName}
              className="w-full h-full rounded-lg shadow-2xl bg-white"
              style={{ minHeight: '70vh' }}
            />
          )}

          {!isImage && !isPdf && (
            <div className="flex flex-col items-center gap-4 text-base-content/60">
              <FiDownload className="text-5xl" />
              <p className="text-sm">{currentName}</p>
              <a href={currentUrl} download={currentName} className="btn btn-primary">
                Download file
              </a>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <ChatImageEditor
          imageUrl={currentUrl}
          imageName={currentName}
          contactUsername={contactUsername}
          onSave={handleSaved}
          onClose={() => setEditing(false)}
        />
      )}
    </>
  );
};

export default ChatMediaViewer;
